import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { CustomerProfile, PurchasedReward, Reward } from '@/models';
import { GETTERS as CUSTOMER_GETTERS } from '@/store/customer';
import { RewardsEvent } from '@/services/analytics/events/rewards-event';

@Component({
    name: 'CmgCyobRewardModal'
})

export default class CmgCyobRewardModal extends Vue {
    errorState = false;
    isLoading = false;
    selectedReward: Reward | null = null;

    @Watch('visible', { immediate: true })
    onVisibleChanged(newVal: boolean) {
        if (newVal && this.customer && !this.hasSeenCyobRewardsModal) {
            this.customer.cyobLastSeenYear = this.mostRecentBirthYear;
            this.$services.customer.updateCustomer({ cyobLastSeenYear: this.mostRecentBirthYear });
        }
    }

    get args() {
        return this.$appStore.state.ui.modals.cyobRewardModal.args;
    }

    get showLaterOption(): boolean {
        return this.args?.showLaterOption || false;
    }

    get hasSeenCyobRewardsModal(): boolean {
        return this.$appStore.getters[CUSTOMER_GETTERS.HAS_SEEN_CYOB_REWARDS_MODAL];
    }

    get mostRecentBirthYear(): number | undefined {
        return this.$appStore.getters[CUSTOMER_GETTERS.MOST_RECENT_BIRTHDAY_YEAR];
    }

    get cyobRewardDeadline(): string {
        const customerBirthday = this.$appStore.state.customer.profile?.dateOfBirth;
        if(!customerBirthday) {
            return '';
        }
        customerBirthday?.setDate(customerBirthday.getDate() + 30);
        return customerBirthday.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'short',
            day: 'numeric'
        });
    }
    
    get birthdayMedallion(): string {
        return this.$appStore.state.cms.images.loyalty.birthdayMedallion.default;
    }

    get visible(): boolean {
        return this.$appStore.state.ui.modals.cyobRewardModal.visible;
    }

    get isMobile(): boolean {
        return this.$services.ui.isMobile();
    }

    get rewards(): Reward[] {
        return this.$appStore.state.reward.cyobRewards;
    }

    get isServiceOnline(): boolean {
        return !this.$appStore.state.reward.unreachable;
    }

    get isRewardsOnline(): boolean {
        return this.$appStore.state.featureFlags.orderingStatus?.isRewardsOnline ?? true;
    }

    get offlineMessage(): string {
        return `We're having some trouble getting your rewards.`;
    }

    get customer(): CustomerProfile | null {
        const profile = this.$appStore.state.customer.profile;
        return profile && !profile.isGuest ? profile : null;
    }

    onClose() {
        this.$services.ui.setModalVisibility('cyobRewardModal', false);
        this.selectedReward = null;
        this.errorState = false;
        this.isLoading = false;
    }

    selectReward(reward: Reward) {
        this.selectedReward = reward;
        this.errorState = false;
    }

    async onClickChooseReward(selectedReward: Reward | null) {        
        if (!selectedReward) {
            this.errorState = true;
            return;
        }

        this.isLoading = true;

        const reward = selectedReward;
        let promoDetails: PurchasedReward | undefined = undefined;

        await this.$services.reward
            .purchaseReward(selectedReward.id)
            .then(async purchasedReward => {
                if (purchasedReward) {
                    promoDetails = purchasedReward

                    this.$services.ui.setModalVisibility('cyobRedemptionSuccessModal', true, {
                        purchasedReward: purchasedReward,
                        reward: reward
                    });

                    this.onClose();

                    await this.$services.customer.refreshCustomerProfileInfo();
                    
                    this.$services.analytics.send(
                        new RewardsEvent({
                            eventType: 'rewardsAddedToWallet',
                            rewardTitle: reward.title.toUpperCase(),
                            rewardsPath: 'rewards-birthday',
                            rewardsID: purchasedReward.promotionCode,
                            rewardsType: purchasedReward.promotionCampaign,
                            pageName: 'rewards:choose-birthday-reward'
                        })
                    );
                }
            })
            .catch(error => {
                if (this.$services.reward.isSparkflyError(error)) {
                    this.$services.ui.setModalVisibility('rewardRedemptionSuccess', true, { reward, showErrorMessage: true });
                    this.$services.reward.setShouldShowRefresh(true);
                } else {
                    this.$services.ui.setModalVisibility('tryAgainModal', true, { onClickTryAgain: () => this.onClickChooseReward(reward) });
                }

                this.$services.analytics.send(
                    new RewardsEvent({
                        eventType: 'error',
                        rewardTitle: reward.title.toUpperCase(),
                        rewardsPath: 'rewards-birthday',
                        rewardsID: promoDetails?.promotionCode,
                        rewardsType: promoDetails?.promotionCampaign,
                        pageName: 'rewards:choose-birthday-reward',
                        error: error
                    })
                );
            })
            .finally(() => {
                this.$services.ui.setAppPepperVisibility(false);
                this.isLoading = false;
                this.onClose();
            });
    }

}